import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheetIcon, FileTextIcon, XIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import z from 'zod';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/entities/database/model/constants';
import { DATABASE_TYPE, type DatabaseItem } from '@/entities/database/model/types';
import { UpdateHistoryDialogContent } from '@/features/database/model/columns';
import { getPresignedUrl, uploadFileToS3 } from '@/features/records/api/file-upload';
import { api, getErrorMessage } from '@/shared/api/api';
import { SwitchCase } from '@/shared/headless';
import { usePagination } from '@/shared/hooks/use-pagination';
import {
  convertPagination,
  type ResponseWithOriginPagination,
  type ResponseWithPagination,
} from '@/shared/types/response';
import type { User } from '@/shared/types/user';
import {
  Input,
  Label,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Text } from '@/shared/ui/text';
import { formatFileSize, truncateFileName } from '@/shared/utils';
import { createDatabaseColumns } from '../model/columns';
import { DataTable } from './data-table';

const formSchema = z.object({
  dataType: z
    .string()
    .refine((value) => value !== null && value !== '', {
      message: '유형을 선택해주세요.',
    })
    .refine((value) => value === DATABASE_TYPE.MANUFACTURE || value === DATABASE_TYPE.PACKAGE, {
      message: '올바른 유형을 선택해주세요.',
    }),
  templateFile: z.instanceof(File, {
    message: '파일을 업로드해주세요.',
  }),
});

interface DatabaseClientProps {
  readonly masterId?: string;
  readonly user: Omit<User, 'is_confirmed'>;
}

export function DatabaseClient({ masterId, user }: DatabaseClientProps) {
  const [filters, setFilters] = useState<{
    type: keyof typeof DATABASE_TYPE | 'ALL';
    perPage: number;
  }>({
    type: 'ALL',
    perPage: DEFAULT_PAGE_SIZE,
  });
  const [formState, setFormState] = useState({
    dataType: '',
    templateFile: null as File | null,
    errors: {
      dataType: '',
      templateFile: '',
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasOpenedRef = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canUpdateDatabase = user.role === 'ADMIN';

  const { mutate: uploadFile, isPending: isUploadingFile } = useMutation({
    mutationFn: async ({
      file,
      dataType,
    }: {
      file: File;
      dataType: keyof typeof DATABASE_TYPE;
    }) => {
      // 1. Presigned URL 발급
      const presignedUrlData = await getPresignedUrl(file, 'MASTER_DATA');

      // 2. S3에 파일 업로드 (새 파일인 경우에만)
      if (presignedUrlData.state === 'new' && presignedUrlData.presigned_url) {
        await uploadFileToS3(file, presignedUrlData.presigned_url, presignedUrlData.content_type);
      }

      // 3. 마스터 데이터 생성
      const result = await api.post<{ master_data_id: string }>('master-data', {
        dk_document_id: presignedUrlData.dk_document_id,
        master_data_type: dataType,
      });

      return result;
    },
    onSuccess: async () => {
      toast.success('DB 업데이트가 완료되었습니다.');
      setFormState({
        dataType: '',
        templateFile: null,
        errors: {
          dataType: '',
          templateFile: '',
        },
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await queryClient.invalidateQueries({
        queryKey: ['database-items', filters.type, filters.perPage, currentPage],
      });

      setIsOpen(false);
    },
    onError: async (error) => {
      const errorMessage = await getErrorMessage(error);
      overlay.open(({ isOpen, close }) => (
        <Dialog open={isOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>오류 안내</DialogTitle>
            </DialogHeader>
            <DialogDescription className="whitespace-pre-line">{errorMessage}</DialogDescription>
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  onClick={() => close()}
                  variant="primary"
                >
                  확인
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
    },
  });

  const { data: queryData, isLoading } = useQuery({
    queryKey: ['database-items', filters.type, filters.perPage, currentPage],
    queryFn: async (): Promise<ResponseWithPagination<DatabaseItem>> => {
      const response = await api.get<ResponseWithOriginPagination<DatabaseItem>>(
        `master-data?type=${filters.type}&page=${currentPage}&size=${filters.perPage}`
      );
      return {
        data: response.data,
        pagination: await convertPagination(response.pagination),
      };
    },
    placeholderData: keepPreviousData,
  });

  const { paginationItems } = usePagination(queryData?.pagination?.totalPages || 0, currentPage);

  const getDetailQueryKey = (masterId: string) => ['database-detail', masterId];

  const databaseColumns = useMemo(() => createDatabaseColumns(getDetailQueryKey), []);

  useEffect(() => {
    if (!masterId || !queryData?.data || hasOpenedRef.current) return;

    const targetRow = queryData.data.find((item) => item.master_data_id === masterId);
    if (!targetRow) return;

    hasOpenedRef.current = true;
    overlay.open(({ isOpen, close }) => (
      <Dialog
        onOpenChange={() => {
          close();
          navigate('/database', { replace: true });
        }}
        open={isOpen}
      >
        <UpdateHistoryDialogContent
          queryKey={getDetailQueryKey}
          row={targetRow}
        />
      </Dialog>
    ));
  }, [masterId, queryData?.data, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const dataType = formState.dataType;
    const templateFile = formState.templateFile;

    const validatedFields = formSchema.safeParse({ dataType, templateFile });

    if (!validatedFields.success) {
      setFormState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          dataType: validatedFields.error.flatten().fieldErrors.dataType?.[0] || '',
          templateFile: validatedFields.error.flatten().fieldErrors.templateFile?.[0] || '',
        },
      }));
      return;
    }

    uploadFile({
      file: validatedFields.data.templateFile,
      dataType: validatedFields.data.dataType as keyof typeof DATABASE_TYPE,
    });
  };

  return (
    <div className="w-full h-full flex flex-col p-7 space-y-2">
      <header className="flex flex-col gap-2 shrink-0">
        <Text
          tag="h1"
          typography="typo-title-m-bold"
        >
          DB 관리
        </Text>
        <Text
          className="text-muted-foreground"
          typography="typo-text-xl"
        >
          생성된 데이터들을 관리할 수 있습니다.
        </Text>
      </header>

      <section className="flex-1 mt-6 gap-3 relative flex flex-col min-h-0">
        <div className="w-full flex justify-between">
          <Select
            onValueChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                type: value as keyof typeof DATABASE_TYPE | 'ALL',
              }));
              setCurrentPage(1);
            }}
            value={filters.type}
          >
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="검색" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                defaultChecked
                value="ALL"
              >
                전체
              </SelectItem>
              <SelectItem value={DATABASE_TYPE.MANUFACTURE}>제조지시</SelectItem>
              <SelectItem value={DATABASE_TYPE.PACKAGE}>포장지시</SelectItem>
            </SelectContent>
          </Select>

          <Dialog
            onOpenChange={(bool) => {
              setFormState({
                dataType: '',
                templateFile: null,
                errors: {
                  dataType: '',
                  templateFile: '',
                },
              });
              setIsOpen(bool);
            }}
            open={isOpen}
          >
            {canUpdateDatabase && (
              <DialogTrigger asChild>
                <Button onClick={() => setIsOpen(true)}>DB 업데이트</Button>
              </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>문서 추가</DialogTitle>
                <DialogDescription className="hidden">문서를 추가해주세요.</DialogDescription>
              </DialogHeader>
              <form
                className="flex flex-col gap-4 py-4"
                id="update-form"
                onSubmit={handleSubmit}
              >
                <fieldset className="space-y-3">
                  <Text
                    tag="legend"
                    typography="typo-text-xl-semibold"
                  >
                    1. 업데이트할 데이터 유형을 선택해주세요.
                  </Text>
                  <RadioGroup
                    name="dataType"
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, dataType: value }))
                    }
                    value={formState.dataType}
                  >
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <RadioGroupItem
                          className="sr-only peer"
                          id={DATABASE_TYPE.MANUFACTURE}
                          value={DATABASE_TYPE.MANUFACTURE}
                        />
                        <Label
                          className={
                            'block cursor-pointer rounded-xl border transition hover:bg-accent/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:shadow-sm peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring'
                          }
                          htmlFor={DATABASE_TYPE.MANUFACTURE}
                        >
                          <div className="flex items-center justify-center gap-2 py-6">
                            <FileTextIcon className="size-4" />
                            <Text typography="typo-text-xl-semibold">제조지시 기록서</Text>
                          </div>
                        </Label>
                      </div>

                      <div className="flex-1">
                        <RadioGroupItem
                          className="sr-only peer"
                          id={DATABASE_TYPE.PACKAGE}
                          value={DATABASE_TYPE.PACKAGE}
                        />
                        <Label
                          className={
                            'block cursor-pointer rounded-xl border transition hover:bg-accent/40 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 peer-data-[state=checked]:shadow-sm peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ring'
                          }
                          htmlFor={DATABASE_TYPE.PACKAGE}
                        >
                          <div className="flex items-center justify-center gap-2 py-6">
                            <FileSpreadsheetIcon className="size-4" />
                            <Text typography="typo-text-xl-semibold">포장지시 기록서</Text>
                          </div>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                  {formState.errors.dataType && (
                    <Text
                      className="text-warning"
                      typography="typo-text-l"
                    >
                      {formState.errors.dataType}
                    </Text>
                  )}
                </fieldset>

                <fieldset className="space-y-3">
                  <div className="flex w-full justify-between">
                    <Text
                      tag="legend"
                      typography="typo-text-xl-semibold"
                    >
                      2. 작성하신 템플릿을 업로드 해주세요.
                    </Text>
                    <Label
                      asChild
                      htmlFor="templateFile"
                    >
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        size="default"
                        type="button"
                        variant="outline"
                      >
                        파일 업로드
                      </Button>
                    </Label>
                  </div>
                  <div className="flex-1">
                    <SwitchCase
                      cases={{
                        empty: (
                          <div className="rounded h-[120px] p-3 bg-neutral-200 flex items-center justify-center">
                            <Text
                              className="text-muted-foreground/60 my-auto"
                              typography="typo-text-xl"
                            >
                              업로드된 파일이 없습니다.
                            </Text>
                          </div>
                        ),
                        'not-empty': (
                          <div className="rounded h-[120px] p-3 border">
                            <div className="flex gap-2 items-center">
                              <Text
                                className="block"
                                typography="typo-text-xl"
                              >
                                {truncateFileName(formState.templateFile?.name || '', 20)}(
                                {formatFileSize(formState.templateFile?.size || 0)})
                              </Text>
                              <Button
                                className="rounded-full flex-shrink-0"
                                onClick={() => {
                                  setFormState((prev) => ({
                                    ...prev,
                                    templateFile: null,
                                  }));
                                }}
                                size="xs"
                                type="button"
                                variant="ghost"
                              >
                                <XIcon className="size-3" />
                              </Button>
                            </div>
                          </div>
                        ),
                      }}
                      value={formState.templateFile ? 'not-empty' : 'empty'}
                    />
                    <Input
                      accept=".xlsx, .xls"
                      className="hidden"
                      id="templateFile"
                      name="templateFile"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setFormState((prev) => ({
                            ...prev,
                            templateFile: file,
                          }));
                        }
                      }}
                      ref={fileInputRef}
                      type="file"
                    />
                  </div>
                </fieldset>

                {formState.errors.templateFile && (
                  <Text
                    className="text-warning"
                    typography="typo-text-l"
                  >
                    {formState.errors.templateFile}
                  </Text>
                )}
              </form>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary">취소</Button>
                </DialogClose>
                <Button
                  form="update-form"
                  pending={isUploadingFile}
                  type="submit"
                >
                  업데이트 하기
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <DataTable<DatabaseItem, unknown>
          columns={databaseColumns}
          data={queryData?.data || []}
          enablePagination={false}
          isLoading={isLoading}
        />
      </section>

      <div className="flex items-center justify-center gap-4 py-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} />
            </PaginationItem>
            {paginationItems.map((item) =>
              typeof item === 'object' ? (
                <PaginationItem key={item.key}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    className="cursor-pointer"
                    isActive={currentPage === item}
                    onClick={() => setCurrentPage(item)}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(queryData?.pagination?.totalPages || 1, prev + 1)
                  )
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>
  );
}
